import { useState } from 'react';

import {
  EvaluationLifecycleRoute,
  type LifecycleStageId,
} from '../../components/dashboard/EvaluationLifecycleRoute';

import { LIFECYCLE_STAGES } from './homeContent';

interface HomeLifecycleSectionProps {
  activeStage: LifecycleStageId;
  sectionRef?: React.RefObject<HTMLElement | null>;
}

export function HomeLifecycleSection({
  activeStage,
  sectionRef,
}: HomeLifecycleSectionProps) {
  const [manualStage, setManualStage] = useState<LifecycleStageId | null>(null);
  const displayStage = manualStage ?? activeStage;
  const fallbackStage = LIFECYCLE_STAGES[0];
  const detail =
    LIFECYCLE_STAGES.find(s => s.id === displayStage) ?? fallbackStage;
  if (!detail) {
    return null;
  }

  return (
    <section
      ref={sectionRef}
      className="mt-16 scroll-mt-24"
      aria-labelledby="home-lifecycle-heading"
      data-home-lifecycle-section
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2
            id="home-lifecycle-heading"
            className="text-xl font-semibold text-[var(--se-ink)]"
          >
            Teacher evaluation workflow
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[var(--se-ink-muted)]">
            Danielson-aligned evaluations move through observation, evidence,
            rubric scoring, and conference. Scroll to follow the route, or
            select a stage to see what evaluators do at each step.
          </p>
        </div>
        <p
          className="text-xs font-medium uppercase tracking-wide text-[var(--se-route-teal)]"
          data-home-stage-indicator
        >
          Stage: {detail.title}
        </p>
      </div>

      <div className="mt-6" data-home-lifecycle-band>
        <EvaluationLifecycleRoute activeStage={displayStage} />
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <div
          data-home-stage-detail
          className="rounded-md border border-[var(--se-rule)] bg-[var(--se-porcelain-panel)] p-5"
        >
          <h3 className="text-base font-semibold text-[var(--se-ink)]">
            {detail.title}
          </h3>
          <p className="mt-2 text-sm text-[var(--se-ink-muted)]">
            {detail.summary}
          </p>
          <ul className="mt-4 space-y-2">
            {detail.bullets.map(item => (
              <li
                key={item}
                className="flex gap-2 text-sm text-[var(--se-ink-muted)]"
              >
                <span
                  className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-sm bg-[var(--se-route-teal)]"
                  aria-hidden
                />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <ul
          className="grid gap-2 sm:grid-cols-2 lg:grid-cols-1"
          role="tablist"
          aria-label="Lifecycle stages"
        >
          {LIFECYCLE_STAGES.map(stage => {
            const selected = displayStage === stage.id;
            return (
              <li key={stage.id}>
                <button
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  data-home-stage-tab
                  data-stage={stage.id}
                  onClick={() => setManualStage(stage.id)}
                  className={`w-full rounded-md border px-4 py-3 text-left transition-colors ${
                    selected
                      ? 'border-[var(--se-route-red)] bg-white shadow-sm'
                      : 'border-[var(--se-rule)] bg-[var(--se-porcelain-panel)] hover:border-[var(--se-ink-muted)]'
                  }`}
                >
                  <span className="text-sm font-semibold text-[var(--se-ink)]">
                    {stage.title}
                  </span>
                  <span className="mt-1 block text-xs text-[var(--se-ink-muted)]">
                    {stage.summary}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
